import { AlertCircle, Check, type LucideIcon } from "lucide-react";
import type { ComponentProps, ReactNode } from "react";
import { useCallback, useEffect, useRef } from "react";

import { cn } from "@/utils/utils";

/**
 * One control shell, shared by every text input, textarea and select.
 *
 * A hairline at rest, the accent plus a soft ring on focus, and the same ring
 * in red when the value is rejected. Keeping it in one string is what stops
 * three different field heights appearing on three different screens.
 */
export const controlClass = [
  "w-full rounded-field border border-line-strong bg-surface-raised text-fg",
  "transition-[border-color,box-shadow,background-color] duration-[var(--duration-fast)]",
  "outline-none placeholder:text-fg-subtle",
  "focus-visible:outline-none focus-visible:border-accent focus-visible:ring-[3px] focus-visible:ring-[var(--accent-ring)]",
  "aria-[invalid=true]:border-danger aria-[invalid=true]:focus-visible:ring-[var(--danger-soft)]",
  "disabled:cursor-not-allowed disabled:bg-surface-inset disabled:text-fg-subtle",
  "read-only:bg-surface-sunken",
].join(" ");

/** 32 / 40 / 44 — the same three heights buttons use, so a row lines up. */
export const controlHeight = {
  sm: "h-8 px-2.5 text-subhead",
  md: "h-10 px-3 text-callout",
  lg: "h-11 px-3.5 text-body",
} as const;

type TextareaProps = ComponentProps<"textarea"> & {
  /** Off for a box that must keep one fixed height. */
  autoSize?: boolean;
};

export function Label({ className, ...props }: ComponentProps<"label">) {
  return <label className={cn("text-fg text-subhead font-medium", className)} {...props} />;
}

export function Input({
  className,
  fieldSize = "md",
  ...props
}: ComponentProps<"input"> & { fieldSize?: keyof typeof controlHeight }) {
  return <input className={cn(controlClass, controlHeight[fieldSize], className)} {...props} />;
}

/**
 * A textarea that grows with what is typed in it, between a floor and a
 * ceiling.
 *
 * A fixed box is wrong twice: a one-line note wastes four lines of the form,
 * and a long one hides most of itself behind a scrollbar. It grows to fit and
 * stops at `max-h`, after which it scrolls — a field that can swallow the page
 * is its own problem.
 *
 * The height is released before it is measured, because `scrollHeight` of an
 * element already tall enough for its content is that height, not the content's
 * — without the reset the box could only ever grow.
 */
export function Textarea({ className, autoSize = true, ...props }: TextareaProps) {
  const ref = useRef<HTMLTextAreaElement>(null);

  const fit = useCallback(() => {
    const element = ref.current;
    if (!element || !autoSize) return;
    element.style.height = "auto";
    element.style.height = `${element.scrollHeight}px`;
  }, [autoSize]);

  // On mount and whenever the value is set from outside — loading a record into
  // the form, say — not only as someone types.
  useEffect(fit, [fit, props.value]);

  return (
    <textarea
      ref={ref}
      onInput={fit}
      className={cn(
        controlClass,
        "text-callout min-h-24 px-3 py-2.5",
        // It sizes itself, so a drag handle would only fight the next keystroke.
        autoSize ? "max-h-56 resize-none overflow-y-auto" : "resize-y",
        className,
      )}
      {...props}
    />
  );
}

/**
 * An input with an icon on the left and optional controls on the right.
 *
 * The icons are decorative: the label is what names the field, so repeating it
 * to a screen reader would only add noise.
 */
export function InputGroup({
  icon: Icon,
  trailing,
  className,
  fieldSize = "md",
  ...props
}: ComponentProps<"input"> & {
  icon: LucideIcon;
  trailing?: ReactNode;
  fieldSize?: keyof typeof controlHeight;
}) {
  return (
    <div className="relative">
      <Icon
        aria-hidden
        className="text-fg-subtle pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
      />
      <Input
        fieldSize={fieldSize}
        className={cn("pl-9.5", trailing && "pr-11", className)}
        {...props}
      />
      {trailing ? (
        <div className="absolute top-1/2 right-1.5 -translate-y-1/2">{trailing}</div>
      ) : null}
    </div>
  );
}

interface FieldProps {
  label: ReactNode;
  labelClassName?: string;
  htmlFor: string;
  error?: string | undefined;
  /** Shown only while there is no error — a field says one thing at a time. */
  hint?: ReactNode;
  /** Marks the label and lets the control keep its own `required`. */
  required?: boolean | undefined;
  className?: string;
  children: ReactNode;
}

/**
 * Label, control, and one line of feedback underneath.
 *
 * The error carries an icon as well as red, and is wired to the control by id
 * so a screen reader reads it as part of the field rather than as loose text.
 */
export function Field({
  label,
  labelClassName,
  htmlFor,
  error,
  hint,
  required,
  className,
  children,
}: FieldProps) {
  return (
    <div className={cn("grid gap-1.5", className)}>
      <Label htmlFor={htmlFor} className={labelClassName}>
        {label}
        {required ? (
          <span aria-hidden className="text-danger ml-0.5">
            *
          </span>
        ) : null}
      </Label>
      {children}
      {error ? (
        <p
          id={`${htmlFor}-error`}
          role="alert"
          className="text-danger text-subhead flex items-start gap-1.5"
        >
          <AlertCircle className="mt-px size-3.5 shrink-0" aria-hidden />
          <span>{error}</span>
        </p>
      ) : hint ? (
        <p id={`${htmlFor}-hint`} className="text-fg-subtle text-subhead">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/**
 * A checkbox drawn as a rounded square with a real tick.
 *
 * The native input stays in the tree and keeps the keyboard and the screen
 * reader; only the box you see is ours.
 */
export function Checkbox({
  className,
  label,
  ...props
}: ComponentProps<"input"> & { label?: ReactNode }) {
  const box = (
    <span className="relative inline-grid shrink-0 place-items-center">
      <input type="checkbox" className={cn("peer sr-only", className)} {...props} />
      <span
        aria-hidden
        className={cn(
          "border-line-strong bg-surface-raised grid size-[18px] place-items-center rounded-[6px] border",
          "transition-[background-color,border-color] duration-[var(--duration-fast)]",
          "peer-checked:border-accent peer-checked:bg-accent peer-indeterminate:border-accent peer-indeterminate:bg-accent",
          // The tick lives inside this box, so it cannot be a `peer-` target itself.
          "peer-checked:[&>svg]:opacity-100",
          "peer-focus-visible:ring-[3px] peer-focus-visible:ring-[var(--accent-ring)]",
          "peer-disabled:opacity-40",
        )}
      >
        <Check className="size-3 text-white opacity-0 transition-opacity" strokeWidth={3} />
      </span>
    </span>
  );
  if (!label) return box;
  return (
    <label className="text-callout text-fg flex cursor-pointer items-center gap-2.5">
      {box}
      <span>{label}</span>
    </label>
  );
}

/**
 * A switch, for a setting that takes effect the moment it moves. Anything that
 * only takes effect on Save is a checkbox instead — the shape is the promise.
 */
export function Toggle({
  className,
  label,
  description,
  ...props
}: ComponentProps<"input"> & { label?: ReactNode; description?: ReactNode }) {
  const control = (
    <span className="relative inline-flex shrink-0">
      <input
        type="checkbox"
        role="switch"
        className={cn("peer sr-only", className)}
        {...props}
      />
      <span
        aria-hidden
        className={cn(
          "bg-surface-inset relative inline-block h-[26px] w-[42px] rounded-full",
          "transition-colors duration-[var(--duration-base)] ease-[var(--ease-out-soft)]",
          "after:absolute after:top-[3px] after:left-[3px] after:size-5 after:rounded-full after:bg-white",
          "after:shadow-[0_1px_3px_rgb(0_0_0/0.25)] after:transition-transform after:duration-[var(--duration-base)] after:ease-[var(--ease-out-soft)]",
          "peer-checked:bg-success peer-checked:after:translate-x-4",
          "peer-focus-visible:ring-[3px] peer-focus-visible:ring-[var(--accent-ring)]",
          "peer-disabled:opacity-40",
        )}
      />
    </span>
  );
  if (!label) return control;
  return (
    <label className="flex cursor-pointer items-start justify-between gap-4">
      <span className="min-w-0">
        <span className="text-fg text-callout block font-medium">{label}</span>
        {description ? (
          <span className="text-fg-muted text-subhead mt-0.5 block">{description}</span>
        ) : null}
      </span>
      {control}
    </label>
  );
}

/** A radio, for one choice out of a short visible set. */
export function Radio({
  className,
  label,
  ...props
}: ComponentProps<"input"> & { label?: ReactNode }) {
  const dot = (
    <span className="relative inline-grid shrink-0 place-items-center">
      <input type="radio" className={cn("peer sr-only", className)} {...props} />
      <span
        aria-hidden
        className={cn(
          "border-line-strong bg-surface-raised size-[18px] rounded-full border",
          "transition-[border-color,box-shadow] duration-[var(--duration-fast)]",
          "peer-checked:border-accent peer-checked:border-[5.5px]",
          "peer-focus-visible:ring-[3px] peer-focus-visible:ring-[var(--accent-ring)]",
          "peer-disabled:opacity-40",
        )}
      />
    </span>
  );
  if (!label) return dot;
  return (
    <label className="text-callout text-fg flex cursor-pointer items-center gap-2.5">
      {dot}
      <span>{label}</span>
    </label>
  );
}
